import { autocompletion, CompletionSource } from '@codemirror/autocomplete'
import { MoreHorizRounded } from '@mui/icons-material'
import { Box, IconButton, Stack, TextField } from '@mui/material'
import { memo, MouseEvent, useEffect, useState } from 'react'
import Helper from '../../../../../helpers/Helper'
import NodeCodemirrorTextEditor from '../../NodeCodemirrorTextEditor'

function useDebounce<T>(value: T, delay: number): T {
    const [debounced, setDebounced] = useState(value)

    useEffect(() => {
        const handler = setTimeout(() => setDebounced(value), delay)
        return () => clearTimeout(handler)
    }, [value, delay])

    return debounced
}

interface P {
    index: number
    fieldKey: string
    fieldValue: any
    onKeyChange: (idx: number, newKey: string) => void
    onValueChange: (idx: number, newValue: string) => void
    onMenuOpen: (event: MouseEvent<HTMLElement>, idx: number) => void
    myCompletions: CompletionSource
    disable: boolean
}

const CustomDataRow = ({ index, fieldKey, fieldValue, onKeyChange, onValueChange, onMenuOpen, myCompletions, disable }: P) => {
    const [localKey, setLocalKey] = useState(fieldKey)
    const [localValue, setLocalValue] = useState(
        Array.isArray(fieldValue)
            ? 'Array'
            : typeof fieldValue === 'object'
                ? 'Object'
                : fieldValue?.toString() ?? ''
    )

    const debouncedKey = useDebounce(localKey, 500)
    const debouncedValue = useDebounce(localValue, 500)

    useEffect(() => {
        setLocalKey(fieldKey)
    }, [fieldKey])

    useEffect(() => {
        setLocalValue(
            Array.isArray(fieldValue)
                ? 'Array'
                : typeof fieldValue === 'object'
                    ? 'Object'
                    : fieldValue?.toString() ?? ''
        )
    }, [fieldValue])

    useEffect(() => {
        if (debouncedKey !== fieldKey) {
            onKeyChange(index, debouncedKey)
        }
    }, [debouncedKey, fieldKey, index, onKeyChange])

    useEffect(() => {
        if (!Array.isArray(fieldValue) && typeof fieldValue !== 'object') {
            const parsed = Helper.parsePrimitive(debouncedValue)
            if (parsed !== fieldValue) {
                onValueChange(index, parsed)
            }
        }
    }, [debouncedValue, index, onValueChange])

    return (
        <Stack flexDirection='row' gap={1} alignItems='center'>
            <Box flex={1}>
                <TextField
                    size='small'
                    fullWidth
                    placeholder='Insert the key'
                    value={localKey}
                    onChange={e => setLocalKey(e.target.value)}
                    disabled={disable}
                />
            </Box>
            <Box flex={1} overflow='hidden'>
                <NodeCodemirrorTextEditor
                    disabled={disable || Array.isArray(fieldValue) || typeof fieldValue === 'object'}
                    placeholder='Insert value'
                    value={localValue}
                    onChange={val => setLocalValue(val)}
                    extensions={[autocompletion({ override: [myCompletions] })]}
                />
            </Box>
            <Box>
                <IconButton
                    size='small'
                    onClick={e => onMenuOpen(e, index)}
                    disabled={disable}>
                    <MoreHorizRounded fontSize='small' />
                </IconButton>
            </Box>
        </Stack>
    )
}

export default memo(CustomDataRow)
